import { useState, useMemo, useEffect, useRef } from 'react'
import {
  useLazyGetTrainingModulesQuery,
  useLazyGetCustomModulesQuery,
  useLazyGetCustomModuleQuery,
  useLazyGetModulePreviewQuery
} from '../api/trainingTopics'

export const useTrainingModules = (topicId, language, topicType) => {
  const [selectedModuleId, setSelectedModuleId] = useState(null)
  const [isPreviewOpen, setIsPreviewOpen] = useState(false)
  const [modulePreviewData, setModulePreviewData] = useState(null)
  const [fetchCustomModules, { data: customModules, isFetching: customModulesFetching }] =
    useLazyGetCustomModulesQuery()
  const [fetchTrainingModules, { data: trainingModules, isFetching: trainingModulesFetching }] =
    useLazyGetTrainingModulesQuery()

  const [fetchCustomModule, { data: customModulePreview, isFetching: customPreviewFetching }] =
    useLazyGetCustomModuleQuery()
  const [fetchModulePreview, { data: modulePreview, isFetching: standardPreviewFetching }] =
    useLazyGetModulePreviewQuery()

  const prevSelectedModuleIdRef = useRef()

  useEffect(() => {
    prevSelectedModuleIdRef.current = selectedModuleId
  })

  const isCustomType = topicType === 'CUSTOM'

  const rawModules = isCustomType ? customModules : trainingModules
  const modulesCurrentlyFetching = isCustomType ? customModulesFetching : trainingModulesFetching
  const previewCurrentlyFetching = isCustomType ? customPreviewFetching : standardPreviewFetching

  useEffect(() => {
    if (!topicId) return

    if (isCustomType) {
      fetchCustomModules({ topicId, language })
    } else {
      fetchTrainingModules({ topicId, language })
    }
    setSelectedModuleId(null)
    setIsPreviewOpen(false)
  }, [topicId, language, isCustomType, fetchCustomModules, fetchTrainingModules])

  useEffect(() => {
    if (!selectedModuleId || !topicId) return

    if (isCustomType) {
      fetchCustomModule({ topicId, moduleId: selectedModuleId, language })
    } else {
      fetchModulePreview({ topicId, moduleId: selectedModuleId, language })
    }
  }, [selectedModuleId, topicId, language, isCustomType, fetchCustomModule, fetchModulePreview])

  useEffect(() => {
    const previousModuleId = prevSelectedModuleIdRef.current
    const moduleIdJustChanged = selectedModuleId !== previousModuleId && previousModuleId !== undefined

    if (!isPreviewOpen || !selectedModuleId) {
      if (modulePreviewData !== null) setModulePreviewData(null)
      return
    }

    if (moduleIdJustChanged || previewCurrentlyFetching) {
      if (modulePreviewData !== null) {
        setModulePreviewData(null)
      }
    } else {
      const rtkData = isCustomType ? customModulePreview : modulePreview
      if (modulePreviewData !== rtkData) {
        setModulePreviewData(rtkData || null)
      }
    }
  }, [isPreviewOpen, selectedModuleId, previewCurrentlyFetching, customModulePreview, modulePreview, isCustomType])

  const modules = useMemo(() => {
    return rawModules?.filter(module => module.type !== null) || []
  }, [rawModules])

  const handleModulePreview = moduleId => {
    if (selectedModuleId !== moduleId) {
      setModulePreviewData(null)
    }
    setSelectedModuleId(moduleId)
    setIsPreviewOpen(true)
  }

  const closeModulePreview = () => {
    setSelectedModuleId(null)
    setIsPreviewOpen(false)
  }

  return {
    modules,
    modulesLoading: modulesCurrentlyFetching,
    modulePreview: modulePreviewData,
    previewLoading: previewCurrentlyFetching,
    isPreviewOpen,
    handleModulePreview,
    closeModulePreview
  }
}
